import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MessageCircle, ShoppingBag, ArrowRight, Calendar, Check, Truck, ShieldCheck, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatINR } from "@/lib/utils-format";
import { useCart } from "@/contexts/CartContext";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import Seo, { productSchema, breadcrumbSchema, orgSchema } from "@/components/Seo";
import ProductDeepDive from "@/components/ProductDeepDive";
import ReviewsSection from "@/components/ReviewsSection";
import RelatedProducts from "@/components/RelatedProducts";
import SigniaBadge from "@/components/SigniaBadge";
import SixReasons from "@/components/SixReasons";
import { warrantyYearsFor } from "@/lib/warranty";

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [activeImg, setActiveImg] = useState(0);
    const [side, setSide] = useState("Pair");
    const [color, setColor] = useState("");
    const [settings, setSettings] = useState({ whatsapp_number: "919971925446" });

    useEffect(() => {
        api.get(`/products/${slug}`).then((r) => {
            setProduct(r.data);
            setColor(r.data.colors?.[0] || "");
            setSide(r.data.is_cros ? "Pair" : "Pair");
            if (r.data.color_images && r.data.colors?.[0]) {
                const firstColorImg = r.data.color_images[r.data.colors[0]];
                if (firstColorImg) {
                    const idx = (r.data.gallery || []).indexOf(firstColorImg);
                    if (idx >= 0) setActiveImg(idx);
                }
            }
            // document.title is managed by <Seo/> Helmet — do not override here
        }).catch(() => navigate("/products"));
        api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    }, [slug, navigate]);

    if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Loading…</div>;

    const gallery = product.gallery?.length ? product.gallery : [product.image];
    const warrantyYrs = warrantyYearsFor(product);
    const seoSchema = {
        "@context": "https://schema.org",
        "@graph": [
            orgSchema(),
            productSchema(product),
            breadcrumbSchema([
                { name: "Home", url: "https://signiastylettoix.com/" },
                { name: "Products", url: "https://signiastylettoix.com/products" },
                { name: product.short_name || product.name, url: `https://signiastylettoix.com/products/${product.slug}` },
            ]),
        ],
    };

    const onAddToCart = () => {
        addItem({
            product_slug: product.slug,
            product_name: product.short_name || product.name,
            qty: 1,
            side,
            color,
            price: product.price,
            image: product.image,
        });
        toast.success("Added to cart");
    };

    const onBuyNow = () => {
        addItem({
            product_slug: product.slug,
            product_name: product.short_name || product.name,
            qty: 1,
            side,
            color,
            price: product.price,
            image: product.image,
        });
        navigate("/checkout");
    };

    // Trim long marketing description to 2 sentences max (no long paragraphs on PDP)
    const shortDesc = (() => {
        const d = product.description || "";
        const parts = d.split(/(?<=[.!?])\s+/).slice(0, 2).join(" ");
        return parts || d;
    })();

    return (
        <>
            <Seo
                title={`${product.short_name || product.name} — Slim & Stylish Rechargeable RIC Hearing Aid | Price, Buy Online`}
                description={`${product.short_name || product.name}: ultra-slim SlimRIC design with RealTime Conversation Enhancement, all-day rechargeable battery, Bluetooth streaming for iPhone & Android (ASHA), LE Audio ready. ${formatINR(product.price)} pair · No-Cost EMI from ${product.emi_per_month ? formatINR(product.emi_per_month) : "₹12,500"}/mo · 3-day free trial + ${warrantyYrs}-yr Signia India warranty. Free audiologist fitting nationwide via HearClear.`}
                canonical={`https://signiastylettoix.com/products/${product.slug}`}
                ogImage={product.image}
                schema={seoSchema}
            />
            <section className="pt-12 md:pt-20 pb-10 md:pb-16">
                <div className="container-x">
                    <nav className="text-[12.5px] text-[#4a5568] mb-8" aria-label="Breadcrumb">
                        <Link to="/" className="hover:text-[#003E4D]">Home</Link>
                        <span className="mx-2 text-[#9CA3AF]">/</span>
                        <Link to="/products" className="hover:text-[#003E4D]">Products</Link>
                        <span className="mx-2 text-[#9CA3AF]">/</span>
                        <span className="text-[#1a1a1a]">{product.short_name || product.name}</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
                        {/* Gallery */}
                        <div data-testid="product-gallery">
                            <div className="aspect-square rounded-3xl overflow-hidden bg-[#f3f4f6] relative">
                                <img src={gallery[activeImg]} alt={product.name} className="w-full h-full object-contain p-8" data-testid="product-main-image" />
                                <div className="absolute top-4 left-4 bg-white/95 rounded-lg p-1.5 shadow-[0_6px_18px_-8px_rgba(0,0,0,0.2)]">
                                    <SigniaBadge variant="logo" className="!h-10 md:!h-11" />
                                </div>
                            </div>
                            {gallery.length > 1 && (
                                <div className="mt-4 grid grid-cols-5 gap-3">
                                    {gallery.slice(0, 10).map((g, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setActiveImg(i)}
                                            className={`aspect-square rounded-xl overflow-hidden bg-[#f3f4f6] border-2 ${activeImg === i ? "border-[#003E4D]" : "border-transparent"} transition`}
                                            data-testid={`product-thumb-${i}`}
                                        >
                                            <img src={g} alt="" className="w-full h-full object-contain p-2" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Info */}
                        <div className="lg:sticky lg:top-24 self-start">
                            <p className="eyebrow">{product.series} — {product.technology_level}</p>
                            <h1 className="font-serif text-[36px] md:text-[52px] leading-[1.04] mt-3 text-[#1a1a1a]">{product.name}</h1>
                            <p className="text-[15.5px] text-[#4a5568] leading-relaxed mt-4">{shortDesc}</p>

                            <div className="mt-7 flex items-end gap-4">
                                <p className="text-[36px] font-semibold leading-none text-[#1a1a1a]">{formatINR(product.price)}</p>
                                {product.mrp && product.mrp > product.price && (
                                    <p className="text-[16px] text-[#9CA3AF] line-through mb-1">{formatINR(product.mrp)}</p>
                                )}
                            </div>
                            {product.emi_per_month && (
                                <p className="text-[13.5px] text-[#4a5568] mt-2">No-Cost EMI from <b>{formatINR(product.emi_per_month)}/mo</b> — up to 24 months</p>
                            )}

                            {product.colors?.length > 0 && (
                                <div className="mt-7">
                                    <p className="eyebrow">Color</p>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        {product.colors.map((c) => (
                                            <button
                                                key={c}
                                                onClick={() => {
                                                    setColor(c);
                                                    const mapped = product.color_images?.[c];
                                                    if (mapped) {
                                                        const idx = gallery.indexOf(mapped);
                                                        if (idx >= 0) setActiveImg(idx);
                                                    }
                                                }}
                                                className={`px-4 py-2 rounded-full text-[13px] border transition ${color === c ? "border-[#003E4D] text-[#003E4D] bg-[#003E4D]/[0.05]" : "border-[#E5E7EB] text-[#4a5568] hover:border-[#003E4D]"}`}
                                                data-testid={`color-option-${c.replace(/\s+/g, "-").toLowerCase()}`}
                                            >
                                                {c}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {product.is_cros && (
                                <div className="mt-6">
                                    <p className="eyebrow">Side</p>
                                    <div className="flex gap-2 mt-3">
                                        {["Pair", "Left", "Right"].map((s) => (
                                            <button
                                                key={s}
                                                onClick={() => setSide(s)}
                                                className={`px-4 py-2 rounded-full text-[13px] border transition ${side === s ? "border-[#003E4D] text-[#003E4D] bg-[#003E4D]/[0.05]" : "border-[#E5E7EB] text-[#4a5568] hover:border-[#003E4D]"}`}
                                                data-testid={`side-option-${s.toLowerCase()}`}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <WhatsAppCTA
                                    waNumber={settings.whatsapp_number}
                                    message={`Hi, I want the best price for ${product.short_name}.`}
                                    productSlug={product.slug}
                                    productName={product.short_name}
                                    sourcePage="product-detail"
                                    className="btn-whatsapp justify-center"
                                    data-testid="pdp-whatsapp-cta"
                                >
                                    <MessageCircle size={16} /> Get Best Price
                                </WhatsAppCTA>
                                <Link to="/book-trial" className="btn-secondary justify-center" data-testid="pdp-trial-cta">
                                    <Calendar size={16} /> Book Free Trial
                                </Link>
                                <button onClick={onAddToCart} className="btn-secondary justify-center" data-testid="pdp-add-cart">
                                    <ShoppingBag size={16} /> Add to Cart
                                </button>
                                <button onClick={onBuyNow} className="btn-primary justify-center" data-testid="pdp-buy-now">
                                    Buy Now <ArrowRight size={16} />
                                </button>
                            </div>

                            <div className="mt-8 grid grid-cols-3 gap-4 pt-7 border-t border-[#E5E7EB]">
                                <Mini icon={Truck} label="Free pan-India delivery" />
                                <Mini icon={ShieldCheck} label={`${warrantyYrs}-yr Signia warranty`} testid="pdp-warranty-badge" />
                                <Mini icon={BadgeCheck} label="3-day risk-free trial" />
                            </div>

                            {product.key_features?.length > 0 && (
                                <div className="mt-10">
                                    <p className="eyebrow">Key features</p>
                                    <ul className="mt-4 space-y-3">
                                        {product.key_features.slice(0, 6).map((f, i) => (
                                            <li key={i} className="flex items-start gap-3 text-[14.5px] text-[#1a1a1a]">
                                                <Check size={16} className="text-[#003E4D] mt-1 shrink-0" />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {product.suitable_for && (
                                <div className="mt-8 p-5 rounded-2xl bg-[#f8f9fa] border border-black/5">
                                    <p className="eyebrow">Who is this for?</p>
                                    <p className="text-[14px] text-[#1a1a1a] mt-2 leading-relaxed">{product.suitable_for}</p>
                                    {product.hearing_loss_range && (
                                        <p className="text-[13px] text-[#4a5568] mt-3"><b>Hearing loss range:</b> {product.hearing_loss_range}</p>
                                    )}
                                </div>
                            )}

                            <div className="mt-6 p-4 rounded-2xl bg-[#fff8e6] border border-[#f5d27a]/40 text-[12.5px] text-[#604a07] leading-relaxed">
                                Hearing aid performance depends on a hearing test and fitting. A HearClear audiologist may contact you to confirm the configuration is right for you.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 6 Reasons to Choose — mirror Signia's official brand structure for SEO parity */}
            <SixReasons />

            {/* Suggestions strip — "You may also like" */}
            <RelatedProducts currentSlug={product.slug} />

            {/* Compact deep-dive: specs + honesty + EMI + FAQs only (no long marketing paragraphs) */}
            <ProductDeepDive slug={slug} price={product.price} warrantyYears={warrantyYrs} compact />
            <ReviewsSection slug={slug} />
        </>
    );
}

const Mini = ({ icon: Icon, label, testid }) => (
    <div className="flex items-center gap-2 text-[12.5px] text-[#1a1a1a]" data-testid={testid}>
        <Icon size={16} className="text-[#003E4D] shrink-0" />
        <span>{label}</span>
    </div>
);
